/**
 * contactService — CRUD for users/{uid}/people/{personId}.
 * Direct Firestore reads/writes (same pattern as mobile manual add).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import type { Contact, ContactFormData } from '../types/contacts';

// ─── Helpers ────────────────────────────────────────────────────────────────

const peopleCol = (uid: string) => collection(FIRESTORE_DB, 'users', uid, 'people');
const personDoc = (uid: string, personId: string) => doc(FIRESTORE_DB, 'users', uid, 'people', personId);

function mapContact(docSnap: any): Contact {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    firstName: data.firstName || data.name?.split(' ')[0] || '',
    lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
    nickname: data.nickname || '',
    dateOfBirth: data.dateOfBirth || '',
    profileImageUrl: data.profileImageUrl || data.imageUrl || '',
    phone: data.phone || data.phoneNumber || '',
    email: data.email || '',
    tetherHandle: data.tetherHandle || undefined,
    relationship: data.relationship || '',
    meetingContext: data.meetingContext || '',
    frequency: data.frequency || '',
    intent: data.intent || '',
    notes: data.notes || '',
    isFavourite: data.isFavourite ?? false,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    lastContactDate: data.lastContactDate,
    nextReminderDate: data.nextReminderDate,
    connectionScore: data.connectionScore,
    // PPL-350 — the marker and the active-first ordering read these. This mapper
    // is an explicit projection, so a field not named here simply does not exist
    // for any list built on it.
    remembered: data.remembered === true,
    pausedAt: data.pausedAt,
  };
}

// ─── Read ───────────────────────────────────────────────────────────────────

/** Get all contacts for a user, ordered by first name */
export async function getContacts(uid: string, pageSize = 50, lastDoc?: any): Promise<{ contacts: Contact[]; lastDoc: any }> {
  const constraints: any[] = [orderBy('firstName', 'asc'), limit(pageSize)];
  if (lastDoc) constraints.push(startAfter(lastDoc));

  const q = query(peopleCol(uid), ...constraints);
  const snap = await getDocs(q);
  const contacts = snap.docs.map(mapContact);
  return { contacts, lastDoc: snap.docs[snap.docs.length - 1] || null };
}

/**
 * Primary email + phone for a person, read from the `contacts` SUBcollection —
 * where phone-sync and contact-detail edits actually store them; the parent
 * person doc rarely carries a top-level `email`. Prefers `isPrimary` rows.
 * Row shapes vary by writer: emails have `label === 'email'`, phones have
 * `type === 'phone'` (saveContactsEurW), so match both fields. (PPL-126)
 */
export async function getContactChannels(
  uid: string,
  personId: string,
): Promise<{ email: string; phone: string }> {
  const snap = await getDocs(collection(FIRESTORE_DB, 'users', uid, 'people', personId, 'contacts'));
  let email = '';
  let phone = '';
  let emailPrimary = false;
  let phonePrimary = false;
  snap.forEach((d) => {
    const row = d.data() as { value?: string; label?: string; type?: string; isPrimary?: unknown };
    const value = (row.value || '').trim();
    if (!value) return;
    const isEmail = row.label === 'email' || row.type === 'email' || (value.includes('@') && row.type !== 'phone');
    const isPhone = row.type === 'phone' || row.label === 'phone';
    const primary = !!row.isPrimary;
    if (isEmail && (!email || (primary && !emailPrimary))) {
      email = value;
      emailPrimary = primary;
    } else if (isPhone && (!phone || (primary && !phonePrimary))) {
      phone = value;
      phonePrimary = primary;
    }
  });
  return { email, phone };
}

/** Search contacts by name */
export async function searchContacts(uid: string, searchTerm: string): Promise<Contact[]> {
  // Firestore doesn't support case-insensitive full-text — fetch all and filter client-side
  const q = query(peopleCol(uid), orderBy('firstName', 'asc'), limit(200));
  const snap = await getDocs(q);
  const lower = searchTerm.toLowerCase();
  return snap.docs
    .map(mapContact)
    .filter(
      (c) =>
        c.firstName.toLowerCase().includes(lower) ||
        c.lastName.toLowerCase().includes(lower) ||
        (c.nickname || '').toLowerCase().includes(lower),
    );
}

/** Get a single contact */
export async function getContact(uid: string, personId: string): Promise<Contact | null> {
  const snap = await getDoc(personDoc(uid, personId));
  if (!snap.exists()) return null;
  return mapContact(snap);
}

/** Real-time listener for all contacts */
export function subscribeToContacts(uid: string, callback: (contacts: Contact[]) => void): Unsubscribe {
  const q = query(peopleCol(uid), orderBy('firstName', 'asc'), limit(200));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(mapContact));
  });
}

/** Get favourite contacts only */
export async function getFavourites(uid: string): Promise<Contact[]> {
  const q = query(peopleCol(uid), where('isFavourite', '==', true), orderBy('firstName', 'asc'), limit(50));
  const snap = await getDocs(q);
  return snap.docs.map(mapContact);
}

// ─── Write ──────────────────────────────────────────────────────────────────

/** Add a new contact (manual form entry — tagged as userCreated) */
export async function addContact(uid: string, data: ContactFormData): Promise<string> {
  const docRef = await addDoc(peopleCol(uid), {
    ...data,
    source: 'userCreated',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/** Update an existing contact */
export async function updateContact(uid: string, personId: string, data: Partial<ContactFormData>): Promise<void> {
  await updateDoc(personDoc(uid, personId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/** Delete a contact */
export async function deleteContact(uid: string, personId: string): Promise<void> {
  await deleteDoc(personDoc(uid, personId));
}

/** Toggle favourite status */
export async function toggleFavourite(uid: string, personId: string, current: boolean): Promise<void> {
  await updateDoc(personDoc(uid, personId), {
    isFavourite: !current,
    updatedAt: serverTimestamp(),
  });
}
